import React from 'react';
import { Navigate } from 'react-router-dom';
import { useCognito } from '../contexts/CognitoProvider';
import { signOut } from '../services/authService';
import {
  useSessionProtection,
  useSessionFocusCheck,
  useSessionIdleTimeout,
} from '../hooks/useSessionProtection';
import { useIdleTimer } from '../hooks/useIdleTimer';
import IdleTimerModal from './IdleTimerModal';
import LoadingScreen from './LoadingScreen';
import { logger } from '../utils/logger';
import { SESSION_CONFIG } from '../config/constants';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute Component
 * Wraps portal routes to ensure user is authenticated.
 * Also owns idle-timer tracking, the warning modal, and session protection
 * so these stay mounted for the entire authenticated session.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, logout } = useCognito();

  // Handle session invalid (redirect to login)
  const handleSessionInvalid = () => {
    logout();
    signOut();
  };

  // Idle timer — tracks activity while authenticated
  const idleTimer = useIdleTimer({
    isEnabled: isAuthenticated,
    idleTimeout: SESSION_CONFIG.IDLE_TIMEOUT,  // from env (default 12 hours)
    warningTime: 60 * 1000,                   // 60-second warning
    debounceTime: 500,
    onWarning: (timeRemaining) => {
      logger.debug(`⏰ Session warning: ${Math.floor(timeRemaining / 1000)}s remaining`);
    },
    onSessionExpire: () => {
      logger.warn('❌ Session expired due to inactivity');
      handleSessionInvalid();
    },
    onExtendSession: () => {
      logger.info('✅ Session extended by user');
    },
    trackActivityGlobally: true,
  });

  // Session protection hooks
  useSessionProtection({
    isAuthenticated,
    onSessionInvalid: handleSessionInvalid,
    checkInterval: 5000,
  });

  useSessionFocusCheck({
    isAuthenticated,
    onSessionInvalid: handleSessionInvalid,
  });

  useSessionIdleTimeout({
    isAuthenticated,
    onSessionInvalid: handleSessionInvalid,
  });

  // While checking auth state (on app load/refresh), show loading screen
  if (isLoading) {
    return <LoadingScreen />;
  }

  // If auth check complete but user not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // User authenticated — render children + idle timer modal overlay
  return (
    <>
      {children}
      <IdleTimerModal
        isVisible={idleTimer.isWarningVisible}
        timeRemaining={idleTimer.timeRemaining}
        formattedTime={idleTimer.formattedTime}
        onExtendSession={idleTimer.extendSession}
        onLogout={idleTimer.forceLogout}
      />
    </>
  );
};

export default ProtectedRoute;
